/**
 * AgentMeter SDK for TypeScript
 * TypeScript SDK for AgentMeter usage-based billing API integration
 */

// Export main client
export { AgentMeterClient } from './client';

// Import types and classes for internal use
import type { AgentMeterConfig } from './types';
import { AgentMeterClient } from './client';

// Export all types and interfaces
export type {
  // Configuration
  AgentMeterConfig,
  
  // Core entities
  ApiResponse,
  App,
  Meter,
  Event,
  Usage,
  UsageSummary,
  UsageTimeSeries,
  
  // Request types
  CreateAppRequest,
  UpdateAppRequest,
  CreateMeterRequest,
  UpdateMeterRequest,
  CreateEventRequest,
  
  // Query parameter types
  EventsQueryParams,
  UsageQueryParams,
  UsageTimeSeriesParams,
} from './types';

// Export error classes
export {
  AgentMeterError,
  ValidationError,
  NetworkError,
} from './types';

// Export validation utilities
export {
  validateRequiredString,
  validatePositiveNumber,
  validateEmail,
  validateUrl,
  validateISODate,
  validateApiKey,
  validatePaginationParams,
} from './utils/validation';

// Export HTTP utilities (for advanced users)
export { HttpClient } from './utils/http';
export type { HttpConfig, HttpResponse } from './utils/http';

/**
 * Create a new AgentMeter client instance with API key
 * 
 * @param apiKey - Your AgentMeter API key
 * @param options - Optional configuration settings
 * @returns Configured AgentMeter client
 */
export function createClient(
  apiKey: string,
  options?: Omit<AgentMeterConfig, 'apiKey'>
): AgentMeterClient {
  return new AgentMeterClient({
    apiKey,
    ...options,
  });
}

/**
 * Default export for convenience
 */
export default AgentMeterClient;

// Version information
export const SDK_VERSION = '1.0.0';
export const SDK_NAME = 'agentmeter';

/**
 * SDK Information
 */
export const SDK_INFO = {
  name: SDK_NAME,
  version: SDK_VERSION,
  description: 'TypeScript SDK for AgentMeter usage-based billing API',
  repository: 'https://github.com/agentmeter/agentmeter-sdk-typescript',
  documentation: 'https://docs.agentmeter.com',
} as const;